/**
 * Authentication Hooks
 *
 * Provides hooks for login, logout, and current user management.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearLoginContinuation,
  persistLoginContinuation,
} from '@/components/auth/loginContinuation';
import { INACTIVITY_LOGOUT_REASON } from '@/components/auth/sessionBroadcast';
import { isUnlockPopupWindow } from '@/components/auth/unlockPopup';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from './queryKeys';

// API response types
interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    tenantId: string;
    tenantName: string;
    role: string;
    roleId?: string;
    permissions?: string[];
    phone: string | null;
    isActive: boolean;
    preferredLanguage: string | null;
    forcePasswordChange: boolean;
    linkedToPayaId: boolean;
  };
}

interface CurrentUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  tenantName: string;
  role: string;
  roleId?: string;
  permissions?: string[];
  phone: string | null;
  isActive: boolean;
  preferredLanguage: string | null;
  forcePasswordChange: boolean;
  /** Whether this account signs in with Paya ID. See `GET /auth/me`. */
  linkedToPayaId: boolean;
}

interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Hook to get the current authenticated user
 */
export function useCurrentUser() {
  const { isAuthenticated, setUser } = useAuthStore();

  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: async () => {
      const requestState = useAuthStore.getState();
      const requestUser = requestState.user;
      const requestEpoch = requestState.sessionEpoch;
      const data = await api.get<CurrentUserResponse>('/auth/me');

      // Sync permissions (and other fields) back to auth store
      // This ensures users with stale localStorage data get fresh permissions
      const latestState = useAuthStore.getState();
      const latestUser = latestState.user;
      const stillSameSession =
        requestUser !== null &&
        latestUser !== null &&
        latestState.sessionEpoch === requestEpoch &&
        latestUser.id === requestUser.id &&
        latestUser.tenantId === requestUser.tenantId;

      if (stillSameSession && data.permissions) {
        const currentPerms = latestUser.permissions ?? [];
        const newPerms = data.permissions;
        const permsChanged =
          currentPerms.length !== newPerms.length ||
          currentPerms.some((p, i) => p !== newPerms[i]);
        const nextForcePasswordChange = data.linkedToPayaId
          ? (latestUser.forcePasswordChange ?? false)
          : data.forcePasswordChange;

        if (
          permsChanged ||
          !latestUser.roleId ||
          latestUser.role !== data.role ||
          latestUser.tenantId !== data.tenantId ||
          latestUser.tenantName !== data.tenantName ||
          latestUser.forcePasswordChange !== nextForcePasswordChange
        ) {
          // For a Paya ID account the session-seeded value is authoritative —
          // OIDC handoff seeds false (#1724) while /auth/me only knows the raw
          // column; overwriting it re-engages the forced-password gate the user
          // cannot complete (#1781). The flag is read from the store at write
          // time because the closure's `user` predates the network round-trip
          // and a forced-change completion may have cleared the flag meanwhile.
          setUser({
            ...latestUser,
            tenantId: data.tenantId,
            tenantName: data.tenantName,
            role: data.role,
            roleId: data.roleId,
            permissions: newPerms,
            forcePasswordChange: nextForcePasswordChange,
          });
        }
      }

      return data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error instanceof ApiError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook for user login
 */
/**
 * What both sign-in paths do with a session once they have one.
 *
 * Shared rather than copied: the password login and the Paya ID handoff (#1625)
 * receive the identical payload, and two copies of "which fields land in the
 * store" drift the moment one of them gains a field.
 */
function useStoreSession(): (data: LoginResponse) => void {
  const queryClient = useQueryClient();
  const { startSession, setUser } = useAuthStore();

  return (data: LoginResponse) => {
    startSession(data.accessToken, data.refreshToken);
    setUser({
      id: data.user.id,
      email: data.user.email,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      tenantId: data.user.tenantId,
      tenantName: data.user.tenantName,
      role: data.user.role,
      roleId: data.user.roleId,
      permissions: data.user.permissions ?? [],
      forcePasswordChange: data.user.forcePasswordChange,
    });
    clearLoginContinuation();
    void queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
  };
}

export function useLogin() {
  const storeSession = useStoreSession();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      api.post<LoginResponse>('/auth/login', credentials),
    onSuccess: storeSession,
  });
}

/**
 * Stamp the server-side lock, then drop the access token locally (#2214).
 *
 * Server proof first, then the overlay and the cross-tab broadcast (the
 * store's `lockSession` is what broadcasts). A network/5xx failure must not
 * claim the server locked: keep a local overlay so refresh cannot bypass it.
 * A completion that outlived a logout/login is ignored.
 */
export function useLockSession(): () => Promise<void> {
  return useCallback(async () => {
    // Lock the family with its live refresh token, not one a sibling tab
    // already rotated away: that answers 401 and signs every tab out (#3392).
    useAuthStore.getState().adoptSiblingTokens();
    const started = useAuthStore.getState();
    if (!started.isAuthenticated || started.isLocked) return;
    const epoch = started.sessionEpoch;
    const refreshToken = started.refreshToken;

    const applyLock = (broadcast: boolean) => {
      const latest = useAuthStore.getState();
      if (latest.sessionEpoch !== epoch) return;
      if (!latest.isAuthenticated) return;
      if (latest.isLocked && latest.accessToken === null) return;
      latest.lockSession({ broadcast });
    };

    if (refreshToken) {
      try {
        await api.post('/auth/lock', { refreshToken });
        applyLock(true);
        return;
      } catch (error) {
        if (useAuthStore.getState().sessionEpoch !== epoch) return;
        if (error instanceof ApiError && error.status === 423) {
          applyLock(true);
          return;
        }
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          throw error;
        }
        applyLock(false);
        return;
      }
    }
    applyLock(false);
  }, []);
}

type UnlockMutationContext = {
  epoch: number;
  lockGeneration: number;
  sessionFamilyId: string | null;
};

/**
 * Unlock the current family without minting a new one. Password proof, or a
 * spent step-up grant. Does not replace the user or the query cache.
 */
export function useUnlockSession() {
  return useMutation({
    mutationFn: (input: { password?: string; stepUpId?: string }) => {
      const { refreshToken } = useAuthStore.getState();
      return api.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
        '/auth/unlock',
        {
          refreshToken,
          ...(input.password === undefined ? {} : { password: input.password }),
          ...(input.stepUpId === undefined ? {} : { stepUpId: input.stepUpId }),
        },
      );
    },
    onMutate: (): UnlockMutationContext => {
      const state = useAuthStore.getState();
      return {
        epoch: state.sessionEpoch,
        lockGeneration: state.lockGeneration,
        sessionFamilyId: state.sessionFamilyId,
      };
    },
    onSuccess: (data, _variables, context) => {
      if (context === undefined) return;
      const state = useAuthStore.getState();
      if (!state.isAuthenticated || !state.isLocked) return;
      if (state.sessionEpoch !== context.epoch) return;
      if (state.lockGeneration !== context.lockGeneration) return;
      if (state.sessionFamilyId === null || state.sessionFamilyId !== context.sessionFamilyId) {
        return;
      }
      state.unlockSession(data.accessToken, data.refreshToken);
    },
  });
}

export function useUnlockOidcStart(): () => Promise<string> {
  return useCallback(async () => {
    const { refreshToken } = useAuthStore.getState();
    const { startUrl } = await api.post<{ startUrl: string }>('/auth/unlock/oidc', {
      refreshToken,
    });
    return startUrl;
  }, []);
}

/** Which sign-in this hostname offers. See `GET /auth/context` for why. */
export interface AuthContext {
  readonly oidcEnabled: boolean;
  readonly tenant: { readonly slug: string; readonly name: string } | null;
  /** Whether hostnames name tenants in this deployment. See `GET /auth/context`. */
  readonly multiTenant: boolean;
}

export function useAuthContext() {
  return useQuery({
    queryKey: [...queryKeys.auth.all, 'context'] as const,
    queryFn: () => api.get<AuthContext>('/auth/context'),
    // The answer changes only when the deployment does, and the login page
    // blocks on it — a stale-while-revalidate window here is a blank page.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Redeem the id the apex put in the URL fragment (#1624/#1625).
 *
 * The session was minted at the apex, where the tenant was known; this runs on
 * the tenant's host and trades a single-use id for it. The id is spent on the
 * first call, so this must not be retried.
 */
export function useOidcHandoff() {
  const storeSession = useStoreSession();

  return useMutation({
    mutationFn: (id: string) => api.post<LoginResponse>('/auth/oidc/handoff', { id }),
    onSuccess: (data) => {
      // The popup writes nothing: the opener applies the same family after it
      // accepts origin + source + the identity it captured. Classify from the
      // captured handshake lifecycle (marker or claimed challenge) — not
      // window.name, which the provider hop clears, and not an unmarked
      // same-origin opener after the handshake timed out. A store write here
      // would persist a new family over a real unlock popup.
      if (isUnlockPopupWindow()) return;
      const state = useAuthStore.getState();
      if (
        state.isLocked &&
        state.isAuthenticated &&
        state.user !== null &&
        state.user.id === data.user.id &&
        state.user.tenantId === data.user.tenantId
      ) {
        // OIDC unlock returns the locked family's refresh token. A normal
        // login of the same user mints a new family and must not unlock.
        if (state.refreshToken !== null && data.refreshToken === state.refreshToken) {
          state.unlockSession(data.accessToken, data.refreshToken);
        }
        return;
      }
      storeSession(data);
    },
    retry: false,
  });
}

/** What `POST /auth/logout` answers (#2050). */
interface LogoutResponse {
  success: boolean;
  /**
   * Where to send the browser so the **Paya ID** session ends too. Present
   * only for a session that came from Paya ID.
   */
  logoutUrl?: string;
}

/**
 * Hook for user logout.
 *
 * Clears local state and empties the cache. Prefer {@link useSignOut}, which
 * also takes the browser where the answer says it has to go.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const { clearAuth, refreshToken } = useAuthStore();

  return useMutation({
    mutationFn: () => api.post<LogoutResponse>('/auth/logout', { refreshToken }),
    onSettled: () => {
      // Clear auth state regardless of API result
      clearAuth();
      // Cancel in-flight tenant queries so a later login cannot paint them.
      void queryClient.cancelQueries();
      queryClient.clear();
    },
  });
}

/**
 * Sign out, and follow through wherever the server says (#2050, #2109).
 *
 * Clearing this app's state is only half of a Paya ID sign-out: the provider's
 * cookie survives it and short-circuits the whole login flow, so one click on
 * "sign in" returns the account that just signed out. `logoutUrl` is the hop
 * that ends the provider's session; a password session has none and gets no
 * URL.
 *
 * Lives here rather than in the component that owns the menu **because of how
 * it broke**: clearing the auth store flips the route guard and unmounts that
 * component, and TanStack Query does not run callbacks passed to `mutate()`
 * once the caller has unmounted. The redirect silently never happened while
 * everything else looked right. A promise continuation has no such
 * dependency, so the work lives in one and the caller only awaits it.
 *
 * Nothing here navigates to `/login`: the guard does that as soon as the store
 * is cleared, and reaching for the router after unmount is the same mistake in
 * a different costume.
 */
export type SignOutOptions = {
  reason?: typeof INACTIVITY_LOGOUT_REASON;
  returnTo?: string;
};

function applyInactivityHandoff(options: SignOutOptions | undefined): void {
  if (options === undefined || options.reason !== INACTIVITY_LOGOUT_REASON) return;
  const returnTo = options.returnTo;
  if (returnTo === undefined) {
    persistLoginContinuation({ reason: INACTIVITY_LOGOUT_REASON });
    return;
  }
  persistLoginContinuation({ reason: INACTIVITY_LOGOUT_REASON, returnTo });
  useAuthStore.getState().setRedirectAfterLogin(returnTo);
}

export function useSignOut(): (options?: SignOutOptions) => Promise<void> {
  const logoutMutation = useLogout();

  return useCallback(async (options?: SignOutOptions) => {
    // Persist *before* clearAuth so the LoginPage that mounts when the
    // guard flips can peek the reason without a reload. Storage must not
    // be consumed on that interim page — a Paya ID end-session hop would
    // otherwise lose it before the provider returns (#2212).
    if (options?.reason === INACTIVITY_LOGOUT_REASON) {
      persistLoginContinuation({
        reason: INACTIVITY_LOGOUT_REASON,
        ...(options.returnTo === undefined ? {} : { returnTo: options.returnTo }),
      });
    }
    let logoutUrl: string | undefined;
    try {
      logoutUrl = (await logoutMutation.mutateAsync()).logoutUrl;
    } catch {
      applyInactivityHandoff(options);
      return;
    }
    // mutateAsync resolves after onSettled, so clearAuth has already wiped
    // redirectAfterLogin. Re-publish the return path here.
    applyInactivityHandoff(options);
    if (logoutUrl !== undefined) {
      window.location.assign(logoutUrl);
    }
  }, [logoutMutation]);
}

/**
 * Hook to refresh access token
 */
export function useRefreshToken() {
  return useMutation({
    mutationFn: () => {
      const { refreshToken } = useAuthStore.getState();
      return api.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
        '/auth/refresh',
        { refreshToken }
      );
    },
    onMutate: () => ({ epoch: useAuthStore.getState().sessionEpoch }),
    onSuccess: (data, _variables, context) => {
      const epoch = context?.epoch;
      if (epoch === undefined) return;
      useAuthStore.getState().applyRotatedTokens(epoch, data.accessToken, data.refreshToken);
    },
    onError: (_error, _variables, context) => {
      const epoch = context?.epoch;
      if (epoch === undefined) return;
      useAuthStore.getState().clearAuthIfCurrent(epoch);
    },
  });
}
